// Exports for Timesheet Reports: the summary (a row per group, and per
// sub-group under it) or every entry, as CSV, Excel or PDF. Each export is
// built once as typed columns and rows, then written in the chosen format, so
// the three files always carry the same figures.

import { downloadCsv } from './timesheet';

export const EXPORT_FORMATS = [
  { key: 'xlsx', label: 'Excel', icon: 'bi-file-earmark-excel', help: 'Numbers stay numbers, with a sheet saying what it covers.' },
  { key: 'csv', label: 'CSV', icon: 'bi-filetype-csv', help: 'Plain values for any spreadsheet or import.' },
  { key: 'pdf', label: 'PDF', icon: 'bi-file-earmark-pdf', help: 'A printable landscape report.' },
];

const round = (v, decimals = 2) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round((n + Number.EPSILON) * 10 ** decimals) / 10 ** decimals : null;
};

const ratio = (part, whole) => (whole > 0 ? part / whole : null);

const moneyFigures = (f) => [f?.cost ?? null, f?.revenue ?? null, f?.margin ?? null, f?.noCostHours ?? null, f?.noRevenueHours ?? null];
const MONEY_COLUMNS = [
  { label: 'Cost', type: 'money' },
  { label: 'Projected revenue', type: 'money' },
  { label: 'Margin', type: 'money' },
  { label: 'Hours with no pay rate', type: 'hours' },
  { label: 'Hours with no ProPlan rate', type: 'hours' },
];

/*
  The summary as columns and rows. A sub-group row repeats its group's label
  so a spreadsheet can filter on it; the PDF leaves it blank and indents.
*/
export const buildSummary = ({ report, groupBy, thenBy, groupLabels }) => {
  const withMoney = Boolean(report.financials);
  const columns = [
    { label: groupLabels[groupBy], type: 'text' },
    ...(thenBy ? [{ label: groupLabels[thenBy], type: 'text' }] : []),
    { label: 'Hours', type: 'hours' },
    { label: 'Billable hours', type: 'hours' },
    { label: 'Billable %', type: 'percent' },
    { label: 'R&D hours', type: 'hours' },
    { label: 'Entries', type: 'count' },
    { label: 'People', type: 'count' },
    ...(withMoney ? MONEY_COLUMNS : []),
  ];
  const figures = (g) => [
    g.hours, g.billableHours, ratio(g.billableHours, g.hours), g.rdHours, g.entries, g.employees,
    ...(withMoney ? moneyFigures(g.financials) : []),
  ];
  const rows = [];
  (report.groups || []).forEach((g) => {
    rows.push({ level: 0, cells: [g.label, ...(thenBy ? [''] : []), ...figures(g)] });
    (g.children || []).forEach((c) => rows.push({ level: 1, cells: [g.label, c.label, ...figures(c)] }));
  });
  const t = report.totals || {};
  const totals = [
    'Total', ...(thenBy ? [''] : []),
    t.hours, t.billableHours, ratio(t.billableHours, t.hours), t.rdHours, t.entries, t.employees,
    ...(withMoney ? moneyFigures(report.financials) : []),
  ];
  return { columns, rows, totals };
};

// Every entry as a row. With `depth` 'projects' the task column is left out.
export const buildEntries = ({ data, depth }) => {
  const withMoney = Boolean(data.financials);
  const withTask = depth !== 'projects';
  const columns = [
    { label: 'Date', type: 'text' },
    { label: 'Employee', type: 'text' },
    { label: 'Client', type: 'text' },
    { label: 'Project', type: 'text' },
    ...(withTask ? [{ label: 'Task', type: 'text' }] : []),
    { label: 'Description', type: 'text', wide: true },
    { label: 'Start', type: 'text' },
    { label: 'End', type: 'text' },
    { label: 'Hours', type: 'hours' },
    { label: 'Billable', type: 'text' },
    { label: 'R&D credit', type: 'text' },
    { label: 'Source', type: 'text' },
    ...(withMoney
      ? [{ label: 'Pay rate', type: 'money' }, { label: 'Cost', type: 'money' }, { label: 'ProPlan rate', type: 'money' }, { label: 'Projected revenue', type: 'money' }]
      : []),
  ];
  const rows = (data.entries || []).map((e) => ({
    level: 0,
    cells: [
      e.date, e.employeeName, e.clientName || '', e.projectName || '',
      ...(withTask ? [e.taskPath] : []),
      e.description || '', e.startTime || '', e.endTime || '', e.hours,
      e.billable ? 'Yes' : 'No', e.rdEligible ? 'Yes' : 'No', e.source,
      ...(withMoney ? [e.costRate ?? null, e.cost ?? null, e.revenueRate ?? null, e.revenue ?? null] : []),
    ],
  }));
  const hours = rows.reduce((sum, r) => sum + (Number(r.cells[columns.findIndex((c) => c.label === 'Hours')]) || 0), 0);
  const totals = columns.map((c, i) => (i === 0 ? 'Total' : c.label === 'Hours' ? hours : ''));
  return { columns, rows, totals };
};

// A value as text, for the CSV and the PDF. Blank stays blank.
const asText = (value, type, { pdf = false } = {}) => {
  if (value === null || value === undefined || value === '') return '';
  if (type === 'percent') return `${Math.round(Number(value) * 100)}%`;
  if (type === 'money') {
    const n = round(value);
    return pdf ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : String(n);
  }
  if (type === 'hours') return pdf ? round(value).toFixed(2) : String(round(value));
  return String(value);
};

const EXCEL_FORMATS = { hours: '0.00', percent: '0%', money: '"$"#,##0.00', count: '0' };

const writeExcel = async ({ filename, title, about, columns, rows, totals }) => {
  const XLSX = await import('xlsx');
  const aoa = [
    columns.map((c) => c.label),
    ...rows.map((r) => r.cells.map((v, i) => (columns[i].type === 'text' ? v ?? '' : v ?? null))),
    ...(totals ? [totals] : []),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Number formats, so hours, % and money read as they do on screen.
  for (let r = 1; r < aoa.length; r += 1) {
    columns.forEach((c, i) => {
      const cell = ws[XLSX.utils.encode_cell({ r, c: i })];
      if (cell && cell.t === 'n' && EXCEL_FORMATS[c.type]) cell.z = EXCEL_FORMATS[c.type];
    });
  }
  ws['!cols'] = columns.map((c, i) => ({
    wch: Math.min(c.wide ? 60 : 40, Math.max(c.label.length, ...aoa.slice(1, 200).map((row) => String(row[i] ?? '').length)) + 2),
  }));
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
  const aboutWs = XLSX.utils.aoa_to_sheet(about);
  aboutWs['!cols'] = [{ wch: 20 }, { wch: 70 }];
  XLSX.utils.book_append_sheet(wb, aboutWs, 'About');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

const writePdf = async ({ filename, title, about, columns, rows, totals }) => {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  const marginX = 36;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(20);
  doc.text(title, marginX, 40);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(90);
  const lines = doc.splitTextToSize(about.map(([k, v]) => `${k}: ${v}`).join('     '), pageWidth - marginX * 2);
  doc.text(lines, marginX, 58);

  const nested = rows.some((r) => r.level > 0);
  const right = (type) => type !== 'text';
  autoTable(doc, {
    head: [columns.map((c) => c.label)],
    body: rows.length
      ? rows.map((r) => r.cells.map((v, i) => {
        // A sub-group row leaves its group's name to the row above, indented under it.
        if (r.level > 0 && i === 0) return '';
        const content = asText(v, columns[i].type, { pdf: true });
        return nested && r.level === 0 ? { content, styles: { fontStyle: 'bold' } } : content;
      }))
      : [[{ content: 'Nothing in this range.', colSpan: columns.length, styles: { halign: 'center', textColor: 150 } }]],
    foot: totals ? [totals.map((v, i) => asText(v, columns[i].type, { pdf: true }))] : undefined,
    startY: 58 + lines.length * 11 + 8,
    margin: { left: marginX, right: marginX, bottom: 30 },
    styles: { fontSize: 7.5, cellPadding: 3, overflow: 'linebreak', valign: 'top' },
    headStyles: { fillColor: [51, 51, 51], fontSize: 7.5 },
    footStyles: { fillColor: [236, 238, 240], textColor: 20, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [246, 246, 246] },
    columnStyles: Object.fromEntries(columns.map((c, i) => [i, {
      halign: right(c.type) ? 'right' : 'left',
      ...(c.wide ? { cellWidth: 180 } : {}),
    }])),
    showFoot: 'lastPage',
    didDrawPage: () => {
      doc.setFontSize(8);
      doc.setTextColor(140);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - marginX, pageHeight - 14, { align: 'right' });
    },
  });
  doc.save(`${filename}.pdf`);
};

// Writes the built export as `format` ('csv' | 'xlsx' | 'pdf'). `about` is the
// [label, value] pairs saying what it covers: the Excel's About sheet and the
// PDF's header line.
export const writeExport = async ({ format, ...file }) => {
  if (format === 'xlsx') return writeExcel(file);
  if (format === 'pdf') return writePdf(file);
  const { filename, columns, rows, totals } = file;
  return downloadCsv(`${filename}.csv`, [
    columns.map((c) => c.label),
    ...rows.map((r) => r.cells.map((v, i) => asText(v, columns[i].type))),
    ...(totals ? [totals.map((v, i) => asText(v, columns[i].type))] : []),
  ]);
};
